// Santa's living room during the call: a wide, warm shot where Santa acts with his whole body
// (leaning in to listen, gesturing and laughing while he talks) instead of a talking close-up.
// Like the village, every frame is a pure function of time plus the call's audio levels.
import type { CallPlan, EventId, OpeningId } from '../shared/script.ts';
import { glow, rng } from './village.ts';

const W = 1600;
const H = 900;
const FLOOR = 700;

type Ctx = CanvasRenderingContext2D;

export interface Mood {
  santa: number; // 0..1 loudness of Santa's voice right now
  child: number; // 0..1 loudness of the child's voice right now
  other?: number; // 0..1 loudness of whoever answered the phone (Mrs. Claus or the elf)
}

export type Phase = 'intro' | 'call' | 'outro';

/** Stage directions: who is where, and what just happened. Times are in cabin seconds. */
interface Stage {
  opening: OpeningId;
  phase: Phase;
  phaseAt: number;
  events: Partial<Record<EventId, number>>;
  other: number;
}
const stage: Stage = { opening: 'fireside', phase: 'call', phaseAt: -99, events: {}, other: 0 };
const since = (t: number, at: number | undefined) => (at === undefined ? Infinity : t - at);
const ramp = (x: number) => Math.min(1, Math.max(0, x));
const smooth = (x: number) => ramp(x) * ramp(x) * (3 - 2 * ramp(x));
const DUST = Array.from({ length: 40 }, () => ({ x: Math.random(), y: Math.random(), p: Math.random() * 6.28, s: 0.3 + Math.random() }));

const r = rng(42);
const SNOW = Array.from({ length: 60 }, () => ({ x: r(), y: r(), z: 0.4 + r() * 0.6 }));
const SPARKS = Array.from({ length: 18 }, () => ({ x: r(), p: r(), s: 0.5 + r() }));
const TREE_LIGHTS = Array.from({ length: 40 }, (_, i) => ({ k: i / 40, side: r() * 2 - 1, c: ['#ff4d6d', '#ffd166', '#4cc9f0', '#80ffdb', '#ff9f1c'][i % 5], p: r() * 6.28 }));

// Smoothed acting values, so Santa moves like a person and not like a meter.
const act = { speak: 0, listen: 0, level: 0, last: 0, other: 0 };

function room(c: Ctx, t: number) {
  // log wall
  c.fillStyle = '#4a2716';
  c.fillRect(-600, -500, W + 1200, FLOOR + 500);
  for (let y = -460; y < FLOOR; y += 46) {
    const g = c.createLinearGradient(0, y, 0, y + 46);
    g.addColorStop(0, '#6b3a20');
    g.addColorStop(0.5, '#5a301a');
    g.addColorStop(1, '#3a1d0f');
    c.fillStyle = g;
    c.fillRect(-600, y + 2, W + 1200, 42);
  }
  // ceiling beam + garland with lights
  c.fillStyle = '#2e170b';
  c.fillRect(-600, 40, W + 1200, 34);
  c.strokeStyle = '#1f6f43';
  c.lineWidth = 16;
  c.beginPath();
  for (let x = -600; x <= W + 600; x += 200) {
    c.moveTo(x, 74);
    c.quadraticCurveTo(x + 100, 130, x + 200, 74);
  }
  c.stroke();
  for (let x = -600; x <= W + 600; x += 50) {
    const k = ((x + 600) % 200) / 200;
    const y = 74 + Math.sin(k * Math.PI) * 42;
    const on = 0.55 + 0.45 * Math.sin(t * 2 + x * 0.05);
    const col = ['255,77,109', '255,209,102', '76,201,240'][Math.abs(x / 50) % 3];
    c.fillStyle = `rgba(${col},${on})`;
    c.beginPath();
    c.arc(x, y + 10, 6, 0, 6.283);
    c.fill();
    glow(c, x, y + 10, 22, col, 0.35 * on);
  }
  // floor + rug
  const f = c.createLinearGradient(0, FLOOR, 0, H + 400);
  f.addColorStop(0, '#5c3520');
  f.addColorStop(1, '#2a160b');
  c.fillStyle = f;
  c.fillRect(-600, FLOOR, W + 1200, 800);
  c.strokeStyle = 'rgba(0,0,0,0.25)';
  c.lineWidth = 3;
  for (let x = -600; x < W + 600; x += 90) {
    c.beginPath();
    c.moveTo(x, FLOOR);
    c.lineTo(x + (x - 800) * 0.6, H + 400);
    c.stroke();
  }
  c.fillStyle = '#8c1c2b';
  c.beginPath();
  c.ellipse(860, 800, 470, 80, 0, 0, 6.283);
  c.fill();
  c.strokeStyle = '#e8c372';
  c.lineWidth = 6;
  c.beginPath();
  c.ellipse(860, 800, 430, 64, 0, 0, 6.283);
  c.stroke();
}

function windowView(c: Ctx, t: number) {
  const flare = Math.sin(ramp(since(t, stage.events.aurora) / 12) * Math.PI);
  const x = 250, y = 170, w = 240, h = 300;
  c.fillStyle = '#2e170b';
  c.fillRect(x - 18, y - 18, w + 36, h + 36);
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, '#061033');
  g.addColorStop(1, '#2a3a7a');
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
  c.save();
  c.beginPath();
  c.rect(x, y, w, h);
  c.clip();
  // northern lights
  c.globalCompositeOperation = 'lighter';
  for (const [col, off, a0] of [['61,255,168', 0, 0.35], ['78,224,255', 2, 0.2], ['255,107,213', 4, 0.05]] as const) {
    const a = a0 * (1 + flare * 2.2);
    const ag = c.createLinearGradient(0, y, 0, y + 160);
    ag.addColorStop(0, `rgba(${col},0)`);
    ag.addColorStop(0.8, `rgba(${col},${a})`);
    ag.addColorStop(1, `rgba(${col},0)`);
    c.fillStyle = ag;
    c.beginPath();
    c.moveTo(x, y + 120);
    for (let i = 0; i <= 24; i++) c.lineTo(x + (i / 24) * w, y + 110 + Math.sin(i * 0.5 + t * 0.4 + off) * 26);
    c.lineTo(x + w, y);
    c.lineTo(x, y);
    c.fill();
  }
  c.globalCompositeOperation = 'source-over';
  // fells and snowy ground
  c.fillStyle = '#c9d6f5';
  c.beginPath();
  c.moveTo(x, y + h - 60);
  c.quadraticCurveTo(x + 60, y + h - 110, x + 120, y + h - 80);
  c.quadraticCurveTo(x + 180, y + h - 120, x + w, y + h - 70);
  c.lineTo(x + w, y + h);
  c.lineTo(x, y + h);
  c.fill();
  // Rudolph peeks in now and then, nose first (and stays a while when it's his surprise)
  const cycle = t % 40;
  const asked = since(t, stage.events.rudolph);
  const peek = asked < 10 ? Math.sin(ramp(asked / 10) * Math.PI) ** 0.3 : cycle > 30 && cycle < 36 ? Math.sin(((cycle - 30) / 6) * Math.PI) : 0;
  if (peek > 0) {
    const rx = x + w + 30 - peek * 90, ry = y + h - 120;
    c.fillStyle = '#8a5a36';
    c.beginPath();
    c.ellipse(rx, ry, 34, 26, -0.2, 0, 6.283);
    c.fill();
    c.strokeStyle = '#e1c497';
    c.lineWidth = 5;
    c.beginPath();
    c.moveTo(rx + 6, ry - 22);
    c.lineTo(rx + 2, ry - 62);
    c.moveTo(rx + 3, ry - 44);
    c.lineTo(rx - 14, ry - 54);
    c.moveTo(rx + 20, ry - 20);
    c.lineTo(rx + 30, ry - 56);
    c.stroke();
    c.fillStyle = '#1a1a1a';
    c.beginPath();
    c.arc(rx - 12, ry - 6, 4, 0, 6.283);
    c.fill();
    glow(c, rx - 34, ry + 6, 40, '255,40,40', 0.7 + 0.2 * Math.sin(t * 6));
    c.fillStyle = '#ff2a2a';
    c.beginPath();
    c.arc(rx - 33, ry + 6, 9, 0, 6.283);
    c.fill();
  }
  // snow
  c.fillStyle = '#fff';
  for (const s of SNOW) {
    const sx = x + ((s.x * w + Math.sin(t + s.y * 9) * 10 + t * 4) % w);
    const sy = y + ((s.y * h + t * 26 * s.z) % h);
    c.globalAlpha = 0.5 + s.z * 0.5;
    c.beginPath();
    c.arc(sx, sy, 1.5 + s.z * 1.8, 0, 6.283);
    c.fill();
  }
  c.globalAlpha = 1;
  for (const [fx, fy] of [[x, y], [x + w, y], [x, y + h], [x + w, y + h]]) glow(c, fx, fy, 70, '235,245,255', 0.45);
  c.restore();
  // frame cross + sill with snow
  c.fillStyle = '#2e170b';
  c.fillRect(x + w / 2 - 7, y, 14, h);
  c.fillRect(x, y + h * 0.45, w, 14);
  c.fillRect(x - 30, y + h + 14, w + 60, 18);
  c.fillStyle = '#eef4ff';
  c.beginPath();
  c.ellipse(x + 40, y + h - 2, 40, 9, 0, 3.14, 6.283);
  c.ellipse(x + w - 50, y + h - 2, 50, 10, 0, 3.14, 6.283);
  c.fill();
  // cool moonlight spilling in, greener when the northern lights flare
  glow(c, x + w / 2, y + h / 2, 260, '140,170,255', 0.12);
  if (flare > 0) glow(c, x + w / 2, y + h / 2, 520, '61,255,168', 0.14 * flare);
}

function tree(c: Ctx, t: number) {
  const x = 110, base = FLOOR + 10;
  c.fillStyle = '#4a2f1d';
  c.fillRect(x - 12, base - 40, 24, 40);
  for (let i = 0; i < 6; i++) {
    const w = 40 + i * 26;
    const top = base - 560 + i * 82;
    c.fillStyle = i % 2 ? '#1a5c3a' : '#1f6f43';
    c.beginPath();
    c.moveTo(x, top);
    c.quadraticCurveTo(x + w * 0.5, top + 60, x + w, top + 120);
    c.lineTo(x - w, top + 120);
    c.quadraticCurveTo(x - w * 0.5, top + 60, x, top);
    c.fill();
  }
  for (const l of TREE_LIGHTS) {
    const y = base - 500 + l.k * 470;
    const lx = x + l.side * (26 + l.k * 150) * 0.9;
    const tw = 0.5 + 0.5 * Math.sin(t * 2.5 + l.p);
    c.globalAlpha = 0.5 + tw * 0.5;
    c.fillStyle = l.c;
    c.beginPath();
    c.arc(lx, y, 6, 0, 6.283);
    c.fill();
    c.globalAlpha = 1;
    glow(c, lx, y, 20, '255,220,150', 0.25 * tw);
  }
  glow(c, x, base - 575, 70 + Math.sin(t * 2) * 8, '255,220,120', 0.6);
  c.fillStyle = '#ffd166';
  c.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? 10 : 24;
    c.lineTo(x + Math.cos(a) * rr, base - 575 + Math.sin(a) * rr);
  }
  c.fill();
  // presents
  for (const [dx, col, rib, w] of [[-10, '#e63946', '#ffd166', 70], [70, '#4cc9f0', '#fff', 50], [140, '#ffd166', '#e63946', 60]] as const) {
    c.fillStyle = col;
    c.fillRect(x + dx, base - w * 0.75, w, w * 0.75);
    c.fillStyle = rib;
    c.fillRect(x + dx + w / 2 - 4, base - w * 0.75, 8, w * 0.75);
    c.fillRect(x + dx, base - w * 0.45, w, 7);
  }
}

function clock(c: Ctx, t: number) {
  const x = 1085, y = 190;
  c.fillStyle = '#3a1d0f';
  c.beginPath();
  c.roundRect(x - 38, y - 46, 76, 200, 16);
  c.fill();
  c.fillStyle = '#f3e6c8';
  c.beginPath();
  c.arc(x, y, 32, 0, 6.283);
  c.fill();
  const now = new Date();
  const hA = ((now.getHours() % 12) + now.getMinutes() / 60) * (Math.PI / 6) - Math.PI / 2;
  const mA = now.getMinutes() * (Math.PI / 30) - Math.PI / 2;
  c.strokeStyle = '#2b2b2b';
  c.lineCap = 'round';
  c.lineWidth = 4;
  c.beginPath();
  c.moveTo(x, y);
  c.lineTo(x + Math.cos(hA) * 16, y + Math.sin(hA) * 16);
  c.moveTo(x, y);
  c.lineTo(x + Math.cos(mA) * 24, y + Math.sin(mA) * 24);
  c.stroke();
  const swing = Math.sin(t * Math.PI) * 0.28;
  c.strokeStyle = '#c9a227';
  c.lineWidth = 3;
  c.beginPath();
  c.moveTo(x, y + 40);
  c.lineTo(x + Math.sin(swing) * 90, y + 40 + Math.cos(swing) * 90);
  c.stroke();
  c.fillStyle = '#e0b84a';
  c.beginPath();
  c.arc(x + Math.sin(swing) * 90, y + 40 + Math.cos(swing) * 90, 12, 0, 6.283);
  c.fill();
}

function fireplace(c: Ctx, t: number) {
  const x = 1150, w = 330;
  // stone chimney breast
  c.fillStyle = '#6d6a72';
  c.fillRect(x, 120, w, FLOOR - 120);
  c.fillStyle = 'rgba(0,0,0,0.18)';
  for (let row = 0; row * 40 + 130 < FLOOR; row++) {
    for (let col = 0; col < 6; col++) {
      const sx = x + col * 60 + (row % 2) * 30 - 10;
      c.beginPath();
      c.roundRect(sx + 4, 130 + row * 40, 52, 34, 10);
      c.fill();
    }
  }
  // wreath
  c.strokeStyle = '#1f6f43';
  c.lineWidth = 26;
  c.beginPath();
  c.arc(x + w / 2, 270, 62, 0, 6.283);
  c.stroke();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * 6.283;
    c.fillStyle = i % 2 ? '#e63946' : '#ffd166';
    c.beginPath();
    c.arc(x + w / 2 + Math.cos(a) * 62, 270 + Math.sin(a) * 62, 6, 0, 6.283);
    c.fill();
  }
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.moveTo(x + w / 2, 330);
  c.lineTo(x + w / 2 - 30, 360);
  c.lineTo(x + w / 2 - 14, 336);
  c.lineTo(x + w / 2 + 14, 336);
  c.lineTo(x + w / 2 + 30, 360);
  c.fill();
  // mantel with stockings
  c.fillStyle = '#3a1d0f';
  c.fillRect(x - 24, 430, w + 48, 30);
  const sway = Math.sin(t * 1.2) * 0.04;
  for (const [sx, col] of [[x + 50, '#c1121f'], [x + 140, '#2d9a5f'], [x + 230, '#c1121f']] as const) {
    c.save();
    c.translate(sx, 460);
    c.rotate(sway);
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(-18, 0);
    c.lineTo(18, 0);
    c.lineTo(18, 60);
    c.quadraticCurveTo(20, 84, 46, 84);
    c.quadraticCurveTo(52, 100, 30, 102);
    c.lineTo(0, 102);
    c.quadraticCurveTo(-20, 100, -18, 70);
    c.fill();
    c.fillStyle = '#fff';
    c.fillRect(-22, -4, 44, 18);
    c.restore();
  }
  // candles on the mantel
  for (const cx of [x + 10, x + w - 10]) {
    c.fillStyle = '#f3e6c8';
    c.fillRect(cx - 7, 390, 14, 40);
    const fl = 1 + Math.sin(t * 13 + cx) * 0.15;
    glow(c, cx, 382, 40, '255,190,90', 0.5);
    c.fillStyle = '#ffd166';
    c.beginPath();
    c.ellipse(cx, 382, 5, 10 * fl, 0, 0, 6.283);
    c.fill();
  }
  // firebox
  const fx = x + 70, fw = 190, fy = 540;
  c.fillStyle = '#140a06';
  c.beginPath();
  c.roundRect(fx, fy, fw, FLOOR - fy, [80, 80, 0, 0]);
  c.fill();
  // logs
  c.fillStyle = '#5a3420';
  c.save();
  c.translate(fx + fw / 2, FLOOR - 18);
  c.rotate(0.12);
  c.fillRect(-80, -12, 160, 24);
  c.rotate(-0.26);
  c.fillRect(-75, -12, 150, 22);
  c.restore();
  // flames: layered teardrops with flicker
  c.save();
  c.globalCompositeOperation = 'lighter';
  const flames = [
    ['255,90,20', 70, 150, 0],
    ['255,140,30', 55, 120, 1.7],
    ['255,200,80', 38, 90, 3.1],
    ['255,240,180', 20, 55, 4.4],
  ] as const;
  for (const [col, fw2, fh, ph] of flames) {
    for (const dx of [-38, 0, 38]) {
      const k = 0.85 + 0.15 * Math.sin(t * 9 + ph + dx) + 0.08 * Math.sin(t * 23 + dx);
      const bx = fx + fw / 2 + dx * 0.9;
      const by = FLOOR - 26;
      const hh = fh * k * (dx === 0 ? 1 : 0.75);
      const lean = Math.sin(t * 3 + dx + ph) * 10;
      c.fillStyle = `rgba(${col},0.55)`;
      c.beginPath();
      c.moveTo(bx - fw2 * 0.5, by);
      c.quadraticCurveTo(bx - fw2 * 0.6, by - hh * 0.5, bx + lean, by - hh);
      c.quadraticCurveTo(bx + fw2 * 0.6, by - hh * 0.5, bx + fw2 * 0.5, by);
      c.fill();
    }
  }
  // sparks drifting up
  for (const s of SPARKS) {
    const k = (t * 0.45 * s.s + s.p) % 1;
    const sx = fx + 40 + s.x * (fw - 80) + Math.sin(k * 12 + s.p * 9) * 12;
    const sy = FLOOR - 40 - k * 170;
    c.fillStyle = `rgba(255,200,90,${(1 - k) * 0.9})`;
    c.beginPath();
    c.arc(sx, sy, 2.2, 0, 6.283);
    c.fill();
  }
  c.restore();
}

function cat(c: Ctx, t: number) {
  // Sleeping by the fire. Breathes; the tail tip twitches now and then.
  const ck = since(t, stage.events.cat);
  if (ck < Infinity && ck >= 1.4) return; // off to Santa's lap
  const x = 1100, y = 790 - (ck < 1.4 ? Math.sin((ck / 1.4) * Math.PI) * 10 : 0);
  const breath = 1 + Math.sin(t * 1.6) * 0.03;
  c.fillStyle = '#e08e45';
  c.beginPath();
  c.ellipse(x, y, 62 * breath, 34 * breath, 0, 0, 6.283);
  c.fill();
  c.fillStyle = '#c6722f';
  for (const dx of [-30, -8, 14]) {
    c.beginPath();
    c.ellipse(x + dx, y - 22, 6, 12, 0.3, 0, 6.283);
    c.fill();
  }
  c.fillStyle = '#e08e45';
  c.beginPath();
  c.arc(x + 52, y - 4, 26, 0, 6.283);
  c.fill();
  c.beginPath();
  c.moveTo(x + 36, y - 20);
  c.lineTo(x + 40, y - 46);
  c.lineTo(x + 56, y - 28);
  c.moveTo(x + 58, y - 28);
  c.lineTo(x + 74, y - 44);
  c.lineTo(x + 76, y - 18);
  c.fill();
  c.strokeStyle = '#5a3420';
  c.lineWidth = 3;
  c.beginPath();
  c.arc(x + 46, y - 6, 6, 0.2, 2.9);
  c.moveTo(x + 66, y - 6);
  c.arc(x + 60, y - 6, 6, 0.2, 2.9);
  c.stroke();
  const twitch = Math.max(0, Math.sin(t * 0.7)) ** 8 * 0.6;
  c.strokeStyle = '#e08e45';
  c.lineWidth = 14;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(x - 55, y + 10);
  c.quadraticCurveTo(x - 30, y + 46, x + 20 + twitch * 20, y + 36 - twitch * 30);
  c.stroke();
  // z z z
  c.fillStyle = 'rgba(255,255,255,0.6)';
  c.font = '600 22px Fredoka, sans-serif';
  const z = (t * 0.5) % 1;
  c.globalAlpha = 1 - z;
  c.fillText('z', x + 70 + z * 20, y - 50 - z * 40);
  c.globalAlpha = 1;
}

function sideTable(c: Ctx, t: number) {
  const x = 980, top = 560;
  c.fillStyle = '#3a1d0f';
  c.fillRect(x - 70, top, 140, 14);
  c.fillRect(x - 60, top + 14, 10, FLOOR + 20 - top);
  c.fillRect(x + 50, top + 14, 10, FLOOR + 20 - top);
  // lamp
  glow(c, x + 30, top - 120, 200, '255,200,120', 0.35);
  c.fillStyle = '#2b2b2b';
  c.fillRect(x + 27, top - 100, 6, 100);
  c.fillStyle = '#f2c86b';
  c.beginPath();
  c.moveTo(x - 5, top - 100);
  c.lineTo(x + 65, top - 100);
  c.lineTo(x + 50, top - 160);
  c.lineTo(x + 10, top - 160);
  c.fill();
  // old phone base
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.moveTo(x - 60, top);
  c.lineTo(x - 50, top - 34);
  c.quadraticCurveTo(x - 25, top - 44, x, top - 34);
  c.lineTo(x + 8, top);
  c.fill();
  c.fillStyle = '#f1e3c8';
  c.beginPath();
  c.arc(x - 26, top - 18, 12, 0, 6.283);
  c.fill();
  // cocoa with steam
  c.fillStyle = '#fff';
  c.fillRect(x + 20, top - 28, 26, 28);
  c.strokeStyle = '#fff';
  c.lineWidth = 4;
  c.beginPath();
  c.arc(x + 48, top - 14, 8, -1.4, 1.4);
  c.stroke();
  c.strokeStyle = 'rgba(255,255,255,0.35)';
  c.lineWidth = 3;
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.4 + i * 0.5) % 1;
    c.globalAlpha = 1 - k;
    c.beginPath();
    c.moveTo(x + 28 + i * 10, top - 32 - k * 50);
    c.bezierCurveTo(x + 18 + i * 10, top - 48 - k * 50, x + 40 + i * 10, top - 60 - k * 50, x + 28 + i * 10, top - 76 - k * 50);
    c.stroke();
  }
  c.globalAlpha = 1;
}

function elf(c: Ctx, t: number, listen: number, onPhone = false) {
  // The record-keeping elf writes in the Nice List whenever the child speaks.
  const x = 520, y = FLOOR + 30;
  c.fillStyle = '#5a3420';
  c.fillRect(x - 40, y - 40, 80, 40);
  // book
  c.fillStyle = '#7a1420';
  c.fillRect(x - 6, y - 74, 80, 12);
  c.fillStyle = '#fff8e6';
  c.beginPath();
  c.moveTo(x - 4, y - 74);
  c.quadraticCurveTo(x + 16, y - 86, x + 36, y - 76);
  c.quadraticCurveTo(x + 56, y - 86, x + 76, y - 74);
  c.lineTo(x + 76, y - 70);
  c.lineTo(x - 4, y - 70);
  c.fill();
  const bob = Math.sin(t * 2) * 2;
  c.fillStyle = '#2d9a5f';
  c.beginPath();
  c.moveTo(x - 10, y - 130 + bob);
  c.lineTo(x + 20, y - 40);
  c.lineTo(x - 40, y - 40);
  c.fill();
  c.fillStyle = '#f6c7a8';
  c.beginPath();
  c.arc(x - 10, y - 140 + bob, 18, 0, 6.283);
  c.fill();
  c.beginPath();
  c.moveTo(x + 4, y - 146 + bob);
  c.lineTo(x + 22, y - 156 + bob);
  c.lineTo(x + 6, y - 136 + bob);
  c.fill();
  c.fillStyle = '#e63946';
  c.beginPath();
  c.moveTo(x - 30, y - 150 + bob);
  c.lineTo(x + 10, y - 150 + bob);
  c.quadraticCurveTo(x, y - 190 + bob, x - 40, y - 196 + bob);
  c.fill();
  c.fillStyle = '#fff';
  c.beginPath();
  c.arc(x - 40, y - 196 + bob, 7, 0, 6.283);
  c.fill();
  c.fillRect(x - 32, y - 154 + bob, 44, 8);
  c.fillStyle = '#2b2b2b';
  c.beginPath();
  c.arc(x - 2, y - 142 + bob, 2.5, 0, 6.283);
  c.fill();
  if (onPhone) {
    // Pipa the switchboard elf talking into a receiver
    c.strokeStyle = '#2d9a5f';
    c.lineWidth = 9;
    c.beginPath();
    c.moveTo(x - 6, y - 100 + bob);
    c.lineTo(x + 6, y - 140 + bob);
    c.stroke();
    c.fillStyle = '#a4161a';
    c.save();
    c.translate(x + 8, y - 146 + bob);
    c.rotate(0.4);
    c.beginPath();
    c.roundRect(-6, -22, 12, 44, 6);
    c.fill();
    c.restore();
    return;
  }
  // writing hand with quill
  const write = listen > 0.2 ? Math.sin(t * 14) * 6 : 0;
  const hx = x + 30 + write, hy = y - 80 + Math.abs(write) * 0.4;
  c.strokeStyle = '#2d9a5f';
  c.lineWidth = 9;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(x - 6, y - 100 + bob);
  c.lineTo(hx, hy);
  c.stroke();
  c.strokeStyle = '#f5f0e6';
  c.lineWidth = 3;
  c.beginPath();
  c.moveTo(hx, hy);
  c.quadraticCurveTo(hx + 10, hy - 30, hx + 26, hy - 46);
  c.stroke();
}

function santa(c: Ctx, t: number, a: typeof act) {
  // Pose from the smoothed audio: lean in to listen, sit back and gesture to talk, rock when idle.
  const onPhone = stage.phase !== 'intro';
  const fromCall = stage.phase === 'call' ? t - stage.phaseAt : 99;
  const asleep = stage.opening === 'nap' && stage.phase === 'intro';
  const startle = stage.opening === 'nap' && fromCall < 0.8 ? Math.sin((fromCall / 0.8) * Math.PI) : 0;
  const snowy = stage.opening === 'stable' ? (stage.phase === 'intro' ? 1 : ramp(1 - fromCall / 40)) : 0;
  const rock = Math.sin(t * 0.9) * 0.035 * (1 - a.listen) * (1 - a.speak * 0.6);
  const lean = a.listen * 0.12 - a.speak * 0.03;
  const laugh = Math.max(0, a.level - 0.55) * 2;
  const jiggle = Math.sin(t * 22) * laugh * 4;
  const px = 760, py = 760; // rocker contact point

  c.save();
  c.translate(px, py - startle * 30);
  c.rotate(rock);

  // rocking chair: rockers, back, seat
  c.strokeStyle = '#4a2a14';
  c.lineWidth = 14;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(-170, -6);
  c.quadraticCurveTo(0, 22, 170, -14);
  c.stroke();
  c.fillStyle = '#5c3218';
  c.fillRect(-120, -150, 16, 145);
  c.fillRect(100, -150, 16, 145);
  c.beginPath();
  c.roundRect(-150, -470, 260, 330, [120, 120, 20, 20]);
  c.fill();
  c.fillStyle = '#7a1c24';
  c.beginPath();
  c.roundRect(-130, -440, 220, 290, [100, 100, 16, 16]);
  c.fill();
  c.strokeStyle = 'rgba(255,209,102,0.35)';
  c.lineWidth = 4;
  for (let i = -100; i < 90; i += 36) {
    c.beginPath();
    c.moveTo(i, -430);
    c.lineTo(i, -160);
    c.stroke();
  }
  c.fillStyle = '#5c3218';
  c.fillRect(-150, -170, 300, 26);

  // Santa from the hips up leans with the pose
  c.save();
  c.translate(0, -160);
  c.rotate(lean);

  // legs (towards viewer, slightly to the right) + boots on the rug
  c.fillStyle = '#b3111f';
  c.beginPath();
  c.roundRect(-60, -10, 150, 64, 30);
  c.fill();
  c.beginPath();
  c.roundRect(40, 0, 56, 150, 26);
  c.roundRect(-40, 4, 56, 146, 26);
  c.fill();
  c.fillStyle = '#1b1b1b';
  c.beginPath();
  c.roundRect(-48, 130, 80, 40, 16);
  c.roundRect(32, 128, 84, 42, 16);
  c.fill();
  c.fillStyle = '#fff';
  c.fillRect(-46, 118, 66, 16);
  c.fillRect(36, 116, 66, 16);

  // tartan blanket over his knees, boots peeking out
  c.save();
  c.beginPath();
  c.moveTo(-90, -30);
  c.quadraticCurveTo(10, -50, 120, -30);
  c.lineTo(130, 112);
  c.quadraticCurveTo(20, 128, -86, 108);
  c.closePath();
  c.fillStyle = '#1f5f3f';
  c.fill();
  c.clip();
  c.globalAlpha = 0.55;
  c.fillStyle = '#b3111f';
  for (let i = -100; i < 140; i += 44) c.fillRect(i, -60, 16, 200);
  for (let j = -40; j < 130; j += 44) c.fillRect(-100, j, 240, 16);
  c.globalAlpha = 0.5;
  c.fillStyle = '#ffd166';
  for (let i = -82; i < 140; i += 44) c.fillRect(i, -60, 3, 200);
  for (let j = -22; j < 130; j += 44) c.fillRect(-100, j, 240, 3);
  c.restore();
  c.fillStyle = '#e8d9a8';
  for (let i = -80; i < 130; i += 12) c.fillRect(i, 110 + Math.sin(i) * 3, 4, 12);

  // body
  c.translate(0, jiggle * 0.5);
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.ellipse(10, -100, 112, 120, 0, 0, 6.283);
  c.fill();
  c.fillStyle = 'rgba(0,0,0,0.12)';
  c.beginPath();
  c.ellipse(60, -90, 60, 110, 0, -1.2, 1.4);
  c.fill();
  c.fillStyle = '#fbfbfb';
  c.fillRect(-4, -210, 28, 200);
  c.fillStyle = '#1b1b1b';
  c.fillRect(-100, -62, 222, 26);
  c.fillStyle = '#ffd166';
  c.fillRect(-12, -66, 44, 34);
  c.fillStyle = '#1b1b1b';
  c.fillRect(-2, -58, 24, 18);

  // free arm (viewer's right): rests on the belly, gestures while talking
  const gesture = a.speak * (0.5 + 0.5 * Math.sin(t * 2.3)) * 0.9;
  c.save();
  c.translate(-80, -150);
  c.rotate(-0.9 + gesture * 1.1);
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.roundRect(-22, 0, 44, 110, 22);
  c.fill();
  c.fillStyle = '#fbfbfb';
  c.fillRect(-24, 96, 48, 18);
  c.fillStyle = '#f6c7a8';
  c.beginPath();
  c.arc(0, 128, 18, 0, 6.283);
  c.fill();
  c.restore();

  // head, tilting in to listen
  const tilt = asleep ? 0.28 + Math.sin(t * 0.8) * 0.03 : a.listen * 0.16 + Math.sin(t * 1.3) * 0.03 * a.speak;
  c.save();
  c.translate(10, -230 + (asleep ? 16 : 0));
  c.rotate(tilt);
  // hat
  const flop = Math.sin(t * 1.1) * 0.08 + rock * 2;
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.moveTo(-58, -40);
  c.quadraticCurveTo(-30, -150, 50, -120);
  c.quadraticCurveTo(110 + flop * 80, -100, 120 + flop * 60, -40);
  c.lineTo(64, -40);
  c.fill();
  c.fillStyle = '#fbfbfb';
  c.beginPath();
  c.arc(122 + flop * 60, -34, 18, 0, 6.283);
  c.fill();
  // face
  c.fillStyle = '#f6c7a8';
  c.beginPath();
  c.ellipse(0, 0, 54, 58, 0, 0, 6.283);
  c.fill();
  c.fillStyle = 'rgba(255,130,130,0.5)';
  c.beginPath();
  c.ellipse(-28, 14, 14, 9, 0, 0, 6.283);
  c.ellipse(30, 14, 14, 9, 0, 0, 6.283);
  c.fill();
  c.fillStyle = '#fbfbfb';
  c.beginPath();
  c.roundRect(-66, -52, 132, 30, 15);
  c.fill();
  // eyes: open while listening, happy arcs while laughing, blink now and then
  const blink = asleep || (t % 4.3) < 0.14;
  c.strokeStyle = '#2b2b2b';
  c.fillStyle = '#2b2b2b';
  c.lineWidth = 4;
  for (const ex of [-20, 22]) {
    c.beginPath();
    if (laugh > 0.2 || blink) {
      c.arc(ex, -2, 7, Math.PI * 1.15, Math.PI * 1.85);
      c.stroke();
    } else {
      c.ellipse(ex, -4, 5, 6.5, 0, 0, 6.283);
      c.fill();
    }
  }
  // glasses with a glint of firelight
  c.strokeStyle = '#c9a227';
  c.lineWidth = 3;
  c.beginPath();
  c.arc(-20, -2, 15, 0, 6.283);
  c.moveTo(37, -2);
  c.arc(22, -2, 15, 0, 6.283);
  c.moveTo(-5, -2);
  c.lineTo(7, -2);
  c.stroke();
  c.fillStyle = `rgba(255,230,180,${0.4 + 0.3 * Math.sin(t * 5)})`;
  c.fillRect(26, -12, 5, 3);
  // eyebrows
  c.strokeStyle = '#f2f2f2';
  c.lineWidth = 7;
  c.lineCap = 'round';
  const brow = a.listen * 4 + laugh * 3;
  c.beginPath();
  c.moveTo(-34, -20 - brow);
  c.quadraticCurveTo(-20, -28 - brow, -8, -21 - brow);
  c.moveTo(8, -21 - brow);
  c.quadraticCurveTo(22, -28 - brow, 36, -20 - brow);
  c.stroke();
  // nose
  c.fillStyle = '#ef9f86';
  c.beginPath();
  c.ellipse(1, 14, 11, 10, 0, 0, 6.283);
  c.fill();
  // beard: bounces with his voice; it covers the mouth, so no lip-sync needed
  const bounce = a.level * 9;
  c.fillStyle = '#fbfbfb';
  c.beginPath();
  c.moveTo(-58, 6);
  c.quadraticCurveTo(-78, 90 + bounce, -30, 128 + bounce);
  c.quadraticCurveTo(0, 150 + bounce * 1.4, 32, 128 + bounce);
  c.quadraticCurveTo(80, 90 + bounce, 58, 6);
  c.quadraticCurveTo(30, 40, 0, 34);
  c.quadraticCurveTo(-30, 40, -58, 6);
  c.fill();
  c.fillStyle = 'rgba(200,210,230,0.5)';
  for (const [bx, by] of [[-30, 70], [10, 90], [34, 64], [-6, 116]]) {
    c.beginPath();
    c.arc(bx, by + bounce, 9, 0, 3.14);
    c.fill();
  }
  // moustache
  c.fillStyle = '#fbfbfb';
  c.beginPath();
  c.ellipse(-17, 30 + bounce * 0.3, 22, 11, 0.25 - a.level * 0.2, 0, 6.283);
  c.ellipse(19, 30 + bounce * 0.3, 22, 11, -0.25 + a.level * 0.2, 0, 6.283);
  c.fill();
  c.restore();

  // a dusting of snow from the stable, melting away
  if (snowy > 0) {
    c.fillStyle = `rgba(255,255,255,${0.9 * snowy})`;
    // flat drifts on the hat's fold and the shoulders, plus a few flakes
    for (const [sx, sy, rx, ry, rot] of [[-10, -352, 34, 8, -0.35], [52, -350, 26, 7, 0.3], [-82, -204, 30, 8, -0.4], [104, -200, 30, 8, 0.4]]) {
      c.beginPath();
      c.ellipse(sx + 10, sy, rx, ry, rot, 0, 6.283);
      c.fill();
    }
    for (const [fx, fy] of [[-30, -300], [60, -310], [-70, -160], [110, -150], [20, -120]]) {
      c.beginPath();
      c.arc(fx, fy, 3, 0, 6.283);
      c.fill();
    }
  }

  if (!onPhone) {
    // receiver still on the table: his hand rests on the armrest
    c.save();
    c.translate(96, -150);
    c.rotate(-0.5);
    c.fillStyle = '#c1121f';
    c.beginPath();
    c.roundRect(-22, 0, 44, 100, 22);
    c.fill();
    c.fillStyle = '#f6c7a8';
    c.beginPath();
    c.arc(0, 116, 18, 0, 6.283);
    c.fill();
    c.restore();
  }

  // phone arm (viewer's right): receiver held to his ear the whole call
  if (onPhone) {
  c.save();
  c.translate(96, -150);
  c.rotate(0.25 + tilt * 0.3);
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.roundRect(-24, -100, 48, 120, 24);
  c.fill();
  c.restore();
  c.save();
  c.translate(74, -240);
  c.rotate(0.45 + tilt);
  c.fillStyle = '#a4161a';
  c.beginPath();
  c.roundRect(-14, -46, 28, 92, 14);
  c.fill();
  c.beginPath();
  c.ellipse(0, -46, 20, 14, 0, 0, 6.283);
  c.ellipse(0, 46, 20, 14, 0, 0, 6.283);
  c.fill();
  c.fillStyle = '#f6c7a8';
  c.beginPath();
  c.arc(16, 10, 20, 0, 6.283);
  c.fill();
  c.restore();
  }
  c.restore(); // lean

  // armrests in front of him: he's sitting in the chair, not standing before it
  c.fillStyle = '#5c3218';
  c.beginPath();
  c.roundRect(-166, -300, 46, 18, 9);
  c.roundRect(118, -300, 46, 18, 9);
  c.fill();
  c.fillRect(-150, -290, 14, 140);
  c.fillRect(134, -290, 14, 140);

  c.restore(); // rock

  if (asleep) {
    c.fillStyle = 'rgba(255,255,255,0.8)';
    c.font = '700 34px Fredoka, sans-serif';
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.4 + i / 3) % 1;
      c.globalAlpha = Math.sin(k * Math.PI);
      c.fillText('Z', 840 + k * 60 + i * 6, 330 - k * 120);
    }
    c.globalAlpha = 1;
  }
  if (!onPhone) return;

  // the curly phone cord back to the side table
  c.strokeStyle = '#8e1018';
  c.lineWidth = 3;
  c.beginPath();
  const sx = px + 100, sy = py - 360;
  const ex = 950, ey = 545;
  for (let i = 0; i <= 60; i++) {
    const k = i / 60;
    const x = sx + (ex - sx) * k;
    const y = sy + (ey - sy) * k + Math.sin(k * Math.PI) * 60;
    const coil = Math.sin(k * 50) * 5;
    i ? c.lineTo(x, y + coil) : c.moveTo(x, y);
  }
  c.stroke();
}


function routeMap(c: Ctx, t: number) {
  // Santa's Christmas Eve route, pinned to the wall
  const x = 880, y = 110, w = 150, h = 104;
  c.fillStyle = '#2e170b';
  c.fillRect(x - 8, y - 8, w + 16, h + 16);
  c.fillStyle = '#f1e2b8';
  c.fillRect(x, y, w, h);
  c.fillStyle = '#a7c58a';
  for (const [cx, cy, rx, ry] of [[30, 34, 22, 16], [44, 72, 12, 20], [84, 30, 20, 13], [90, 64, 14, 20], [124, 40, 18, 16], [130, 82, 10, 8]]) {
    c.beginPath();
    c.ellipse(x + cx, y + cy, rx, ry, 0.3, 0, 6.283);
    c.fill();
  }
  c.strokeStyle = '#c1121f';
  c.setLineDash([4, 4]);
  c.lineWidth = 2;
  c.beginPath();
  c.moveTo(x + 86, y + 12);
  c.bezierCurveTo(x + 140, y + 30, x + 120, y + 90, x + 70, y + 70);
  c.bezierCurveTo(x + 20, y + 60, x + 10, y + 30, x + 40, y + 24);
  c.stroke();
  c.setLineDash([]);
  // a tiny sleigh travels the route
  const k = (t * 0.05) % 1;
  c.fillStyle = '#ffd166';
  c.beginPath();
  c.arc(x + 86 + Math.sin(k * 6.283) * 40, y + 50 + Math.cos(k * 6.283) * 26, 3, 0, 6.283);
  c.fill();
  for (const [px, py] of [[86, 12], [40, 24], [70, 70]]) {
    c.fillStyle = '#c1121f';
    c.beginPath();
    c.arc(x + px, y + py, 4, 0, 6.283);
    c.fill();
  }
}

function toyTrain(c: Ctx, t: number, front: boolean) {
  // A wind-up train circling the tree; drawn in two passes so it goes behind and in front.
  const cx = 110, cy = FLOOR + 30, rx = 190, ry = 34;
  if (!front) {
    c.strokeStyle = '#3a2618';
    c.lineWidth = 6;
    c.beginPath();
    c.ellipse(cx, cy, rx, ry, 0, 0, 6.283);
    c.stroke();
  }
  for (let i = 0; i < 4; i++) {
    const a = t * 0.6 - i * 0.32;
    const inFront = Math.sin(a) > 0;
    if (inFront !== front) continue;
    const x = cx + Math.cos(a) * rx, y = cy + Math.sin(a) * ry;
    const col = i === 0 ? '#c1121f' : ['#2d9a5f', '#ffd166', '#4cc9f0'][i - 1];
    c.fillStyle = col;
    c.beginPath();
    c.roundRect(x - 18, y - 26, 36, 22, 4);
    c.fill();
    c.fillStyle = '#1b1b1b';
    c.beginPath();
    c.arc(x - 10, y - 3, 5, 0, 6.283);
    c.arc(x + 10, y - 3, 5, 0, 6.283);
    c.fill();
    if (i === 0) {
      c.fillStyle = '#1b1b1b';
      c.fillRect(x + 4, y - 40, 8, 14);
      for (let j = 0; j < 3; j++) {
        const k = (t * 1.2 + j / 3) % 1;
        c.fillStyle = `rgba(240,240,250,${0.6 * (1 - k)})`;
        c.beginPath();
        c.arc(x + 8 - Math.cos(a) * k * 30, y - 44 - k * 30, 4 + k * 8, 0, 6.283);
        c.fill();
      }
    }
  }
}

function teddy(c: Ctx) {
  const x = 395, y = FLOOR + 24;
  c.fillStyle = '#a0683a';
  c.beginPath();
  c.ellipse(x, y - 30, 28, 32, 0, 0, 6.283);
  c.arc(x, y - 78, 24, 0, 6.283);
  c.arc(x - 18, y - 98, 9, 0, 6.283);
  c.arc(x + 18, y - 98, 9, 0, 6.283);
  c.ellipse(x - 26, y - 4, 14, 10, 0, 0, 6.283);
  c.ellipse(x + 26, y - 4, 14, 10, 0, 0, 6.283);
  c.fill();
  c.fillStyle = '#e3b98c';
  c.beginPath();
  c.ellipse(x, y - 70, 11, 8, 0, 0, 6.283);
  c.fill();
  c.fillStyle = '#1b1b1b';
  c.beginPath();
  c.arc(x - 8, y - 84, 3, 0, 6.283);
  c.arc(x + 8, y - 84, 3, 0, 6.283);
  c.arc(x, y - 72, 3.5, 0, 6.283);
  c.fill();
  c.fillStyle = '#c1121f';
  c.beginPath();
  c.moveTo(x - 12, y - 56);
  c.lineTo(x + 12, y - 56);
  c.lineTo(x, y - 48);
  c.fill();
}

function plate(c: Ctx, t: number) {
  // the gingerbread Mrs. Claus brings in
  if ((stage.events as any).cookies === undefined || since(t, (stage.events as any).cookies) < 4.2) return;
  const x = 930, y = 556;
  c.fillStyle = '#f5f5f5';
  c.beginPath();
  c.ellipse(x, y, 30, 8, 0, 0, 6.283);
  c.fill();
  for (const dx of [-14, 2, 16]) {
    c.fillStyle = '#b5651d';
    c.beginPath();
    c.arc(x + dx, y - 12, 6, 0, 6.283);
    c.ellipse(x + dx, y - 3, 6, 7, 0, 0, 6.283);
    c.fill();
  }
}

function dust(c: Ctx, t: number) {
  // motes glittering in the lamp and fire light
  c.save();
  c.globalCompositeOperation = 'lighter';
  for (const d of DUST) {
    const x = 860 + d.x * 600 + Math.sin(t * 0.3 * d.s + d.p) * 30;
    const y = 200 + ((d.y * 500 - t * 6 * d.s) % 500 + 500) % 500;
    c.fillStyle = `rgba(255,220,170,${0.25 + 0.25 * Math.sin(t * 2 + d.p)})`;
    c.beginPath();
    c.arc(x, y, 1.6, 0, 6.283);
    c.fill();
  }
  c.restore();
}

function santaShadow(c: Ctx, t: number) {
  // the fire is on the right, so Santa's shadow falls big and soft on the wall to his left
  const f = 1 + 0.04 * Math.sin(t * 7.3) + 0.02 * Math.sin(t * 17);
  c.save();
  c.translate(640, 620);
  c.scale(f * 0.9, f * 0.9);
  c.fillStyle = 'rgba(20,8,4,0.2)';
  c.beginPath();
  c.ellipse(0, -150, 120, 150, -0.1, 0, 6.283);
  c.ellipse(20, -330, 60, 64, 0, 0, 6.283);
  c.moveTo(-30, -380);
  c.quadraticCurveTo(20, -480, 110, -420);
  c.lineTo(60, -370);
  c.fill();
  c.restore();
}

/** Mrs. Claus: red dress, white apron, green shawl, silver bun. */
function mrsClaus(c: Ctx, x: number, feet: number, dir: number, walking: boolean, talk: number, pose: 'phone' | 'wave' | 'plate' | 'none', t: number) {
  const bob = walking ? Math.abs(Math.sin(t * 7)) * 5 : Math.sin(t * 1.6) * 1.5;
  const nod = talk * Math.sin(t * 9) * 3;
  c.save();
  c.translate(x, feet - bob);
  c.scale(dir, 1);
  // shoes
  const step = walking ? Math.sin(t * 7) * 10 : 0;
  c.fillStyle = '#1b1b1b';
  c.beginPath();
  c.ellipse(-14 + step, 0, 16, 8, 0, 0, 6.283);
  c.ellipse(16 - step, 0, 16, 8, 0, 0, 6.283);
  c.fill();
  // dress
  c.fillStyle = '#b3111f';
  c.beginPath();
  c.moveTo(-36, -200);
  c.lineTo(36, -200);
  c.lineTo(66, -6);
  c.quadraticCurveTo(0, 6, -66, -6);
  c.fill();
  c.fillStyle = '#fff';
  c.fillRect(-66, -18, 132, 12);
  // apron
  c.fillStyle = '#fbf7ee';
  c.beginPath();
  c.moveTo(-28, -150);
  c.lineTo(28, -150);
  c.lineTo(44, -30);
  c.quadraticCurveTo(0, -20, -44, -30);
  c.fill();
  c.fillStyle = '#e63946';
  c.beginPath();
  c.arc(-12, -96, 5, 0, 6.283);
  c.arc(14, -70, 5, 0, 6.283);
  c.fill();
  // shawl
  c.fillStyle = '#1f6f43';
  c.beginPath();
  c.moveTo(-44, -206);
  c.quadraticCurveTo(0, -170, 44, -206);
  c.lineTo(40, -170);
  c.quadraticCurveTo(0, -140, -40, -170);
  c.fill();
  // arms
  const sleeve = (ax: number, ay: number, ang: number, len = 80) => {
    c.save();
    c.translate(ax, ay);
    c.rotate(ang);
    c.fillStyle = '#b3111f';
    c.beginPath();
    c.roundRect(-12, 0, 24, len, 12);
    c.fill();
    c.fillStyle = '#f6c7a8';
    c.beginPath();
    c.arc(0, len + 8, 11, 0, 6.283);
    c.fill();
    c.restore();
  };
  if (pose === 'plate') {
    sleeve(-32, -190, -0.9, 70);
    sleeve(32, -190, -1.3, 70);
    c.fillStyle = '#f5f5f5';
    c.beginPath();
    c.ellipse(78, -168, 34, 9, 0, 0, 6.283);
    c.fill();
    for (const dx of [62, 78, 94]) {
      c.fillStyle = '#b5651d';
      c.beginPath();
      c.arc(dx, -182, 7, 0, 6.283);
      c.ellipse(dx, -172, 7, 8, 0, 0, 6.283);
      c.fill();
    }
  } else {
    sleeve(-32, -192, 0.15);
    if (pose === 'wave') sleeve(32, -192, -2.6 + Math.sin(t * 8) * 0.35);
    else if (pose === 'phone') sleeve(30, -192, -2.6, 52);
    else sleeve(32, -192, -0.15);
  }
  // head
  c.translate(0, nod * 0.3);
  c.fillStyle = '#f6c7a8';
  c.beginPath();
  c.arc(0, -240, 34, 0, 6.283);
  c.fill();
  c.fillStyle = '#e8e8ee';
  c.beginPath();
  c.arc(0, -258, 34, Math.PI, 0);
  c.arc(-6, -288, 18, 0, 6.283);
  c.fill();
  c.fillStyle = 'rgba(255,130,130,0.5)';
  c.beginPath();
  c.arc(-16, -232, 7, 0, 6.283);
  c.arc(18, -232, 7, 0, 6.283);
  c.fill();
  c.strokeStyle = '#c9a227';
  c.lineWidth = 2.5;
  c.beginPath();
  c.arc(-11, -244, 9, 0, 6.283);
  c.moveTo(22, -244);
  c.arc(13, -244, 9, 0, 6.283);
  c.stroke();
  c.fillStyle = '#2b2b2b';
  c.beginPath();
  c.arc(-11, -244, 2.6, 0, 6.283);
  c.arc(13, -244, 2.6, 0, 6.283);
  c.fill();
  c.fillStyle = '#9b2c3a';
  c.beginPath();
  c.ellipse(1, -222, 8, 2.5 + talk * 5, 0, 0, Math.PI);
  c.fill();
  if (pose === 'phone') {
    c.fillStyle = '#a4161a';
    c.save();
    c.translate(40, -238);
    c.rotate(0.35);
    c.beginPath();
    c.roundRect(-9, -34, 18, 68, 9);
    c.ellipse(0, -34, 14, 9, 0, 0, 6.283);
    c.ellipse(0, 34, 14, 9, 0, 0, 6.283);
    c.fill();
    c.restore();
  }
  c.restore();
}

/** Where Mrs. Claus is right now, from the stage directions. */
function mrsClausNow(c: Ctx, t: number) {
  const talk = stage.other;
  const fromPhase = t - stage.phaseAt;
  if (stage.phase === 'intro' && stage.opening !== 'elf') return mrsClaus(c, 600, FLOOR + 60, 1, false, talk, 'phone', t);
  if (stage.phase === 'call' && stage.opening !== 'elf' && fromPhase < 3.5) {
    // hands over the phone, then bustles off to the kitchen
    const k = smooth(fromPhase / 3.5);
    return mrsClaus(c, 600 - k * 900, FLOOR + 60, -1, k > 0.02, 0, 'none', t);
  }
  const ck = since(t, (stage.events as any).cookies);
  if (ck < 9) {
    // walks in with gingerbread, puts it down, walks out
    if (ck < 4) return mrsClaus(c, -300 + smooth(ck / 4) * 1180, FLOOR + 60, 1, true, 0, 'plate', t);
    if (ck < 5) return mrsClaus(c, 880, FLOOR + 60, 1, false, 0, 'none', t);
    return mrsClaus(c, 880 - smooth((ck - 5) / 4) * 1180, FLOOR + 60, -1, true, 0, 'none', t);
  }
  if (stage.phase === 'outro') {
    const k = smooth(fromPhase / 2);
    return mrsClaus(c, -300 + k * 870, FLOOR + 60, 1, k < 1, talk, k < 1 ? 'none' : 'wave', t);
  }
}

function catOnLap(c: Ctx, t: number) {
  const ck = since(t, stage.events.cat);
  if (ck < 2.3) {
    // stretch, then a hop in an arc from the fire to Santa's lap
    if (ck < 1.4) return;
    const k = smooth((ck - 1.4) / 0.9);
    const x = 1100 + (770 - 1100) * k, y = 790 + (590 - 790) * k - Math.sin(k * Math.PI) * 120;
    c.fillStyle = '#e08e45';
    c.beginPath();
    c.ellipse(x, y, 50, 22, -0.2, 0, 6.283);
    c.arc(x - 46, y - 14, 20, 0, 6.283);
    c.fill();
    return;
  }
  c.fillStyle = '#e08e45';
  const breath = 1 + Math.sin(t * 1.6) * 0.03;
  c.beginPath();
  c.ellipse(760, 590, 48 * breath, 26 * breath, 0, 0, 6.283);
  c.arc(800, 580, 20, 0, 6.283);
  c.fill();
  c.beginPath();
  c.moveTo(788, 566);
  c.lineTo(792, 546);
  c.lineTo(804, 562);
  c.moveTo(806, 562);
  c.lineTo(818, 548);
  c.lineTo(818, 570);
  c.fill();
  c.strokeStyle = '#5a3420';
  c.lineWidth = 2.5;
  c.beginPath();
  c.arc(796, 580, 4, 0.2, 2.9);
  c.moveTo(812, 580);
  c.arc(808, 580, 4, 0.2, 2.9);
  c.stroke();
}

function lighting(c: Ctx, t: number) {
  // firelight flickers over the whole room
  const f = 0.85 + 0.1 * Math.sin(t * 7.3) + 0.05 * Math.sin(t * 17.1);
  c.save();
  c.globalCompositeOperation = 'lighter';
  glow(c, 1315, 640, 900, '255,140,60', 0.16 * f);
  glow(c, 1315, 660, 260, '255,170,80', 0.3 * f);
  c.restore();
  // vignette keeps the eye on Santa
  const v = c.createRadialGradient(800, 520, 250, 800, 520, 1150);
  v.addColorStop(0, 'rgba(10,4,2,0)');
  v.addColorStop(1, 'rgba(10,4,2,0.75)');
  c.fillStyle = v;
  c.fillRect(-600, -500, W + 1200, H + 1000);
}

function foreground(c: Ctx) {
  // dark out-of-focus shapes at the edges give the shot depth
  c.fillStyle = 'rgba(12,30,20,0.9)';
  c.beginPath();
  for (let i = 0; i < 7; i++) {
    const x = -140 + i * 34;
    c.moveTo(x, -40);
    c.lineTo(x + 70, 100 + i * 18);
    c.lineTo(x + 120, -40);
  }
  c.fill();
  c.fillStyle = 'rgba(30,10,8,0.9)';
  c.beginPath();
  c.roundRect(1420, 800, 260, 180, 20);
  c.fill();
  c.fillStyle = 'rgba(120,20,30,0.9)';
  c.fillRect(1530, 800, 26, 180);
}

export function startCabin(canvas: HTMLCanvasElement, mood: () => Mood, plan?: Pick<CallPlan, 'opening'>) {
  const c = canvas.getContext('2d')!;
  const t0 = performance.now();
  let dpr = 1, cw = 0, ch = 0;
  let now = 0;
  stage.opening = plan?.opening ?? 'fireside';
  stage.phase = plan ? 'intro' : 'call';
  stage.phaseAt = 0;
  stage.events = {};

  function resize() {
    dpr = Math.min(2, devicePixelRatio || 1);
    cw = canvas.clientWidth;
    ch = canvas.clientHeight;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
  }

  function render(t: number) {
    now = t;
    const m = mood();
    stage.other = act.other = act.other + ((m.other ?? 0) - act.other) * 0.4;
    const dt = Math.min(0.1, Math.max(0, t - act.last));
    act.last = t;
    const ease = (v: number, target: number, rate: number) => v + (target - v) * Math.min(1, dt * rate);
    act.level = ease(act.level, m.santa, 14);
    act.speak = ease(act.speak, m.santa > 0.08 ? 1 : 0, 3);
    act.listen = ease(act.listen, m.child > 0.12 && m.santa < 0.08 ? 1 : 0, act.listen < 0.5 ? 4 : 0.8);

    // camera: frames Santa, drifts slowly, pushes in a little while he talks
    const portrait = ch > cw;
    const base = portrait ? Math.max(cw / 640, ch / 1000) : Math.max(cw / W, ch / H);
    const zoom = base * (1 + act.speak * 0.05 + 0.015 * Math.sin(t * 0.13));
    const focusX = portrait ? 790 : 800 + Math.sin(t * 0.07) * 30;
    const focusY = portrait ? 520 : 470;
    const layer = (depth: number) => {
      const z = zoom * (1 + (depth - 1) * 0.04);
      c.setTransform(dpr * z, 0, 0, dpr * z, dpr * (cw / 2 - focusX * z), dpr * (ch / 2 - focusY * z));
    };

    c.setTransform(1, 0, 0, 1, 0, 0);
    c.fillStyle = '#1a0c06';
    c.fillRect(0, 0, canvas.width, canvas.height);
    layer(0.9);
    room(c, t);
    windowView(c, t);
    clock(c, t);
    routeMap(c, t);
    santaShadow(c, t);
    layer(1);
    toyTrain(c, t, false);
    tree(c, t);
    toyTrain(c, t, true);
    teddy(c);
    fireplace(c, t);
    sideTable(c, t);
    plate(c, t);
    santa(c, t, act);
    if (since(t, stage.events.cat) < Infinity) catOnLap(c, t);
    elf(c, t, act.listen, stage.phase === 'intro' && stage.opening === 'elf');
    cat(c, t);
    mrsClausNow(c, t);
    dust(c, t);
    lighting(c, t);
    layer(1.25);
    foreground(c);
  }

  let running = true;
  const loop = () => {
    if (!running) return;
    render((performance.now() - t0) / 1000);
    requestAnimationFrame(loop);
  };
  resize();
  addEventListener('resize', resize);
  render(0);
  requestAnimationFrame(loop);
  const control = {
    stop() {
      running = false;
      removeEventListener('resize', resize);
    },
    /** intro: someone else holds the phone; call: Santa has it; outro: Mrs. Claus waves goodbye. */
    phase(p: Phase) {
      stage.phase = p;
      stage.phaseAt = now;
    },
    event(id: EventId) {
      stage.events[id] = now;
    },
  };
  (window as any).__cabin = { render, act, stage, ...control };
  return control;
}
